import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  compose,
  withE2ERequestStubbing,
} from '../hooks/useService';
import urls from '../util/urls';
import { ServiceAuth } from '../hooks/useService/useService';
import { useActiveProjectCohortId, useActiveProjectId } from '../hooks';
import { useMemo } from 'react';
import _get from 'lodash/get';

const fileIDRegex =
  /^https:\/\/.*lifeomic.com.*\/files\/([0-9A-Fa-f]{8}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{12})$/;

export const fileServiceUrlToID = (url: string): string | null => {
  const regRes = fileIDRegex.exec(url);
  if (!regRes) return null;
  return _get(regRes, [1], null);
};

export const checkIsFileServiceFile = (url: string) =>
  !!fileServiceUrlToID(url);

interface Options {
  axiosOptions?: AxiosRequestConfig;
  [key: string]: any;
}

export type AddFileResponse = {
  id: string;
  name: string;
  datasetId: string;
  contentType: string;
  userId: string;
  uploadUrl: string;
};

export type GetFileResponse = {
  id: string;
  name: string;
  datasetId?: string;
  size?: number;
  contentType?: string;
  lastModified?: string;
  userId?: string;
  lrn?: string;
  downloadUrl?: string;
};

export type GetFilesResponse = {
  items: GetFileResponse[];
};

export type SubjectUserRestrictionOptions = {
  subjectId: string;
  cohort?: string;
};

export type CohortRestrictionOptions = {
  cohort: string;
};

export type SubjectRestrictionOptions = {
  project: string;
} & (SubjectUserRestrictionOptions | CohortRestrictionOptions);

export type TransferJobSchedule = {
  start: string;
  interval: number;
  timezone: string;
};

export type TransferJobCredentials = {
  awsAccessKeyId: string;
  awsSecretAccessKey: string;
  region: string;
};

export type TransferJobSource = {
  bucket: string;
  prefix?: string;
  credentials?: TransferJobCredentials;
};

export type TransferJobDestination = {
  prefix?: string;
};

export type TransferJob = {
  id?: string;
  name: string;
  project: string;
  move: boolean;
  schedule?: TransferJobSchedule;
  source: TransferJobSource;
  destination?: TransferJobDestination;
};

interface GetTransferJobParams {
  pageSize?: number | null;
  projectId?: string | null;
  nextPageToken?: string | null;
}

export const useSubjectRestrictedOptions = (
  subjectId?: string,
): SubjectRestrictionOptions | undefined => {
  const cohort = useActiveProjectCohortId();
  const project = useActiveProjectId();

  return useMemo(() => {
    if (!cohort && !subjectId) return;

    if (subjectId) {
      return { project, cohort, subjectId };
    } else {
      return { cohort, project };
    }
  }, [cohort, project, subjectId]);
};

const hasSubjectRestriction = (options?: SubjectRestrictionOptions): boolean =>
  (!!options?.cohort ||
    !!(options as SubjectUserRestrictionOptions)?.subjectId) &&
  !!options?.project;
export default class FileService {
  getFilesAPI: (auth: ServiceAuth) => AxiosInstance;
  constructor(_options?: Partial<Options>) {
    this.getFilesAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  addFile =
    (auth: ServiceAuth) =>
    async (
      contentType: string,
      datasetId: string,
      name: string,
      subjectRestrictionOptions?: SubjectRestrictionOptions,
      id?: string,
      /** When true, replaces content of an existing file with the same path (file-service `overwrite`). */
      overwrite?: boolean,
    ) => {
      const subject = (
        subjectRestrictionOptions as SubjectUserRestrictionOptions
      )?.subjectId;
      const url = hasSubjectRestriction(subjectRestrictionOptions)
        ? // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          urls.api.files.patientFile(subjectRestrictionOptions.project, {
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            cohort: subjectRestrictionOptions.cohort || undefined,
            subject,
          })
        : urls.api.files.files({});

      const body: any = {
        contentType,
        datasetId,
        name,
        id,
      };

      if (overwrite) {
        body.overwrite = true;
      }

      if (subject) {
        body.attributes = {
          subject,
        };
        body.include = 'uploadUrl';
      }

      return this.getFilesAPI(auth).post<AddFileResponse>(url, body);
    };

  renameFile =
    (auth: ServiceAuth) =>
    async (
      fileId: string,
      newName: string,
      subjectRestrictionOptions?: SubjectRestrictionOptions,
    ) => {
      const url = hasSubjectRestriction(subjectRestrictionOptions)
        ? urls.api.files.patientFile(
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            subjectRestrictionOptions.project,
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            { cohort: subjectRestrictionOptions.cohort },
            fileId,
          )
        : urls.api.files.files({}, fileId);
      return this.getFilesAPI(auth).patch(url, {
        name: newName,
      });
    };

  deleteFile =
    (auth: ServiceAuth) =>
    async (
      fileId: string,
      subjectRestrictionOptions?: SubjectRestrictionOptions,
    ) => {
      const url = hasSubjectRestriction(subjectRestrictionOptions)
        ? urls.api.files.patientFile(
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            subjectRestrictionOptions.project,
            {
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              cohort: subjectRestrictionOptions.cohort,
              subject: (
                subjectRestrictionOptions as SubjectUserRestrictionOptions
              )?.subjectId,
            },
            fileId,
          )
        : urls.api.files.files({}, fileId);
      return this.getFilesAPI(auth).delete(url);
    };

  fetchDownloadLink =
    (auth: ServiceAuth) =>
    async (
      fileId: string,
      subjectRestrictionOptions?: SubjectRestrictionOptions,
    ) => {
      const baseParams = {
        include: 'downloadUrl',
        includeContentDisposition: true,
      };

      const url = hasSubjectRestriction(subjectRestrictionOptions)
        ? urls.api.files.patientFile(
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            subjectRestrictionOptions.project,
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            { ...baseParams, cohort: subjectRestrictionOptions.cohort },
            fileId,
          )
        : urls.api.files.files(baseParams, fileId);
      return this.getFilesAPI(auth).get(url);
    };

  fetchOpenLink =
    (auth: ServiceAuth) =>
    async (
      fileId: string,
      subjectRestrictionOptions?: SubjectRestrictionOptions,
    ) => {
      const subject = (
        subjectRestrictionOptions as SubjectUserRestrictionOptions
      )?.subjectId;

      const baseParams = {
        include: 'downloadUrl',
        includeContentDisposition: false,
      };

      const url = hasSubjectRestriction(subjectRestrictionOptions)
        ? urls.api.files.patientFile(
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            subjectRestrictionOptions.project,
            {
              ...baseParams,
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              cohort: subjectRestrictionOptions.cohort || undefined,
              subject,
            },
            fileId,
          )
        : urls.api.files.files(baseParams, fileId);
      return this.getFilesAPI(auth).get(url);
    };

  fetchProjectFiles = (auth: ServiceAuth) => async (params: any) => {
    const { projectId, subjectRestrictionOptions, ...restParams } = params;
    const url = hasSubjectRestriction(subjectRestrictionOptions)
      ? urls.api.files.searchPatientFiles(subjectRestrictionOptions.project, {
          ...(restParams || {}),
          cohort: subjectRestrictionOptions.cohort,
        })
      : urls.api.projects.files(restParams, projectId);

    return this.getFilesAPI(auth).get(url);
  };

  fetchFiles =
    (auth: ServiceAuth) =>
    async (
      params: object,
      subjectRestrictionOptions?: SubjectRestrictionOptions,
    ) => {
      const url = hasSubjectRestriction(subjectRestrictionOptions)
        ? // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          urls.api.files.listPatientFiles(subjectRestrictionOptions.project, {
            ...(params || {}),
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            cohort: subjectRestrictionOptions.cohort,
          })
        : urls.api.files.files(params);

      return this.getFilesAPI(auth).get<GetFilesResponse>(url);
    };

  getFile =
    (auth: ServiceAuth) =>
    async (
      fileId: string,
      subjectRestrictionOptions?: SubjectRestrictionOptions,
      params?: any,
    ) => {
      const subject = (
        subjectRestrictionOptions as SubjectUserRestrictionOptions
      )?.subjectId;
      const url = hasSubjectRestriction(subjectRestrictionOptions)
        ? urls.api.files.patientFile(
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            subjectRestrictionOptions.project,
            {
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              cohort: subjectRestrictionOptions.cohort || undefined,
              subject,
              ...(params || {}),
            },
            fileId,
          )
        : urls.api.files.files(
            { include: 'downloadUrl', ...(params || {}) },
            fileId,
          );
      return this.getFilesAPI(auth).get<GetFileResponse>(url);
    };

  createTransferJob = (auth: ServiceAuth) => async (job: TransferJob) => {
    const url = urls.api.files.transferJobs({});
    return this.getFilesAPI(auth).post<TransferJob>(url, job);
  };

  updateTransferJob = (auth: ServiceAuth) => async (job: TransferJob) => {
    const url = urls.api.files.transferJobs({}, job.id);
    return this.getFilesAPI(auth).put<TransferJob>(url, job);
  };

  deleteTransferJob = (auth: ServiceAuth) => async (id: string) => {
    const url = urls.api.files.transferJobs({}, id);
    return this.getFilesAPI(auth).delete(url);
  };

  getTransferJobs =
    (auth: ServiceAuth) => async (params: GetTransferJobParams) => {
      const { pageSize, nextPageToken, projectId } = params;
      const requestParams: GetTransferJobParams = {};

      if (pageSize) {
        requestParams.pageSize = pageSize;
      }

      if (nextPageToken) {
        requestParams.nextPageToken = nextPageToken;
      }

      if (projectId) {
        requestParams.projectId = projectId;
      }

      const url = urls.api.files.transferJobs(requestParams);

      return this.getFilesAPI(auth).get<TransferJob[]>(url);
    };

  startTransferJob = (auth: ServiceAuth) => async (id: string) => {
    const url = urls.api.files.transferJobsRun(id);
    return this.getFilesAPI(auth).put(url);
  };

  stopTransferJob = (auth: ServiceAuth) => async (id: string) => {
    const url = urls.api.files.transferJobsRun(id);
    return this.getFilesAPI(auth).delete(url);
  };

  getTransferJobRun = (auth: ServiceAuth) => async (id: string) => {
    const url = urls.api.files.transferJobsRun(id);
    return this.getFilesAPI(auth).get(url, {
      validateStatus: status =>
        (status >= 200 && status < 400) || status === 404,
    });
  };
}
